import React, { useState, useEffect, useCallback } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi
} from "@/components/ui/carousel";
import TutorialSlideComponent, { TutorialSlide } from './TutorialSlide';

interface TutorialSlideDeckProps {
  slides: TutorialSlide[];
  onSlideChange: (index: number) => void;
  onApiReady?: (api: CarouselApi) => void;
}

const TutorialSlideDeck: React.FC<TutorialSlideDeckProps> = ({ 
  slides, 
  onSlideChange,
  onApiReady 
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const [isFirstSlide, setIsFirstSlide] = useState(true);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!api) return;
    
    switch (event.key) {
      case 'ArrowRight':
        api.scrollNext();
        break;
      case 'ArrowLeft':
        api.scrollPrev();
        break;
    }
  }, [api]);

  useEffect(() => {
    if (!api) return;

    api.on("select", () => {
      const currentIndex = api.selectedScrollSnap();
      setIsFirstSlide(currentIndex === 0);
      onSlideChange(currentIndex);
    });

    // Add keyboard event listeners
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [api, onSlideChange, handleKeyDown]);

  useEffect(() => {
    if (api && onApiReady) {
      onApiReady(api);
    }
  }, [api, onApiReady]);

  return (
    <Carousel 
      setApi={setApi}
      className="flex-1"
      opts={{
        align: 'start',
        loop: false
      }}
      aria-label="Tutorial slides"
    >
      <CarouselContent>
        {slides.map((slide, index) => (
          <CarouselItem 
            key={index} 
            className="flex flex-col items-center justify-center text-center px-4"
            role="group"
            aria-roledescription="slide"
            aria-label={`Slide ${index + 1} of ${slides.length}: ${slide.title}`}
          >
            <TutorialSlideComponent slide={slide} />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );
};

export default TutorialSlideDeck;
